import { sceneConstants } from './constants'
import { normalizeGraphicsQuality } from '../utils/sceneGraphicsQuality'
import {
    allocateSceneCopySuffixes,
    appendSceneInstanceIds,
    buildSceneInstanceRecords,
    createSceneInstanceSeed,
    ensureSceneCopyPaths,
    getSceneInstancePath,
    removeSceneInstanceIds,
} from '../utils/sceneInstanceIdentity'
import { remapSceneObjectReferences } from '../utils/cartSceneObjectSnapshot'

export const resetSceneState = (payload = {}) => ({
    type: 'RESET_SCENE_STATE_TO_DEFAULTS',
    payload,
})

export const hydrateSceneSnapshot = (snapshot = {}, payload = {}) => ({
    type: 'HYDRATE_SCENE_SNAPSHOT',
    payload: {
        snapshot: snapshot || {},
        ...payload,
    },
})

const SET_SCENE_OBJECTS_SOLID_TYPE = 'SET_SCENE_OBJECTS_SOLID'

export const setSceneObjectsSolid = (ids = [], solid = true) => ({
    type: SET_SCENE_OBJECTS_SOLID_TYPE,
    payload: {
        ids: [...new Set((Array.isArray(ids) ? ids : [ids]).filter(Boolean))],
        solid: !!solid,
    },
})

export const setShowPivot = (value) => ({
    type: sceneConstants.SET_SHOW_PIVOT,
    payload: !!value,
})

export const requestPivotPreset = (id, type) => ({
    type: sceneConstants.REQUEST_PIVOT_PRESET,
    payload: { id, type },
})

export const clearPivotRequest = () => ({
    type: sceneConstants.CLEAR_PIVOT_REQUEST,
})

export const applyPivotPresetResolved = (id, pivot, position, presetKey) => ({
    type: sceneConstants.APPLY_PIVOT_PRESET_RESOLVED,
    payload: { id, pivot, position, presetKey },
})

export const setPivotPresetState = (id, presetKey = null) => ({
    type: sceneConstants.SET_PIVOT_PRESET_STATE,
    payload: { id, presetKey },
})

export const setShowTextDimensions = (value) => ({
    type: sceneConstants.SET_SHOW_TEXT_DIMENSIONS,
    payload: !!value,
})

export const setMoveMode = (mode) => ({
    type: sceneConstants.SET_MOVE_MODE,
    payload: mode,
})

export const setMoveStep = (step) => ({
    type: sceneConstants.SET_MOVE_STEP,
    payload: step,
})

export const setCtrlPressed = (isPressed) => ({
    type: sceneConstants.SET_CTRL_PRESSED,
    payload: isPressed,
})

export const setKarkasAndFenceColor = (color) => ({
    type: sceneConstants.SET_KARKAS_AND_FENCE_COLOR,
    payload: color,
})

export const setColors = (colors) => ({
    type: sceneConstants.SET_COLORS,
    payload: colors,
})

export const setMonumentPolished = (isPolished) => ({
    type: sceneConstants.SET_MONUMENT_POLISHED,
    payload: isPolished,
})

export const setShowDimensionLines = (show) => ({
    type: sceneConstants.SET_SHOW_DIMENSION_LINES,
    payload: show,
})

export const setLineThickness = (thickness) => ({
    type: sceneConstants.SET_LINE_THICKNESS,
    payload: thickness,
})

export const setFontSize = (size) => ({
    type: sceneConstants.SET_FONT_SIZE,
    payload: size,
})

export const setLineColor = (color) => ({
    type: sceneConstants.SET_LINE_COLOR,
    payload: color,
})

export const setDimensions = (dimensions) => ({
    type: sceneConstants.SET_DIMENSIONS,
    payload: dimensions,
})

export const setScales = (scales = {}) => ({
    type: sceneConstants.SET_SCALES,
    payload: scales,
})

export const setPivotPoint = (pivotPoint = {}) => ({
    type: sceneConstants.SET_PIVOT_POINT,
    payload: pivotPoint,
})

export const setSelectedModel = (model) => ({
    type: sceneConstants.SET_SELECTED_MODEL,
    payload: model,
})

export const setPositions = (positions = {}) => ({
    type: sceneConstants.SET_POSITIONS,
    payload: positions,
})

export const setRotations = (rotations = {}) => ({
    type: sceneConstants.SET_ROTATIONS,
    payload: rotations,
})

export const setModelPaths = (modelPaths) => ({
    type: sceneConstants.SET_MODEL_PATHS,
    payload: modelPaths,
})

export const setSelectedModels = (selectedModels) => ({
    type: sceneConstants.SET_SELECTED_MODELS,
    payload: selectedModels,
    meta: {
        sceneInstanceSeed: createSceneInstanceSeed(),
    },
})

export const setFenceColor = (color) => ({
    type: sceneConstants.SET_FENCE_COLOR,
    payload: color,
})

export const setSceneColor = (color) => ({
    type: sceneConstants.SET_SCENE_COLOR,
    payload: color,
})

export const setGridVisible = (visible) => ({
    type: sceneConstants.SET_GRID_VISIBLE,
    payload: visible,
})

export const setGridColor = (color) => ({
    type: sceneConstants.SET_GRID_COLOR,
    payload: color,
})

export const setGridPosition = (position) => ({
    type: sceneConstants.SET_GRID_POSITION,
    payload: position,
})

export const setMinZ = (minZ) => ({
    type: sceneConstants.SET_MIN_Z,
    payload: minZ,
})

export const setProcessedPhoto = (photo) => ({
    type: sceneConstants.SET_PROCESSED_PHOTO,
    payload: photo,
})

export const setHeadCenter = (center) => ({
    type: sceneConstants.SET_HEAD_CENTER,
    payload: center,
})

export const setCameraState = (cameraState) => ({
    type: sceneConstants.SET_CAMERA_STATE,
    payload: cameraState,
})

export const toggleHiddenModel = (modelPath) => ({
    type: sceneConstants.TOGGLE_HIDDEN_MODEL,
    payload: modelPath,
})

export const setSelectedModelPaths = (paths = []) => ({
    type: sceneConstants.SET_SELECTED_MODEL_PATHS,
    payload: paths,
})

export const toggleMultiSelectMode = () => ({
    type: sceneConstants.TOGGLE_MULTI_SELECT_MODE,
})

export const upsertSelectionGroup = (group) => ({
    type: sceneConstants.UPSERT_SELECTION_GROUP,
    payload: group,
})

export const setActiveSelectionGroup = (groupId) => ({
    type: sceneConstants.SET_ACTIVE_SELECTION_GROUP,
    payload: groupId,
})

export const setFloorVisible = (value) => ({ type: sceneConstants.SET_FLOOR_VISIBLE, payload: value })
export const setFloorMode = (value) => ({ type: sceneConstants.SET_FLOOR_MODE, payload: value })
export const setFloorColor = (value) => ({ type: sceneConstants.SET_FLOOR_COLOR, payload: value })

export const resetSceneTransformsForTargets = (targets = []) => (dispatch) => {
    const list = Array.isArray(targets) ? [...new Set(targets.filter(Boolean))] : []
    if (!list.length) return

    const nextPos = {}
    const nextRot = {}
    const nextScale = {}

    list.forEach((id) => {
        nextPos[id] = [0, 0, 0]
        nextRot[id] = [0, 0, 0]
        nextScale[id] = [1, 1, 1]
    })

    dispatch(setPositions(nextPos))
    dispatch(setRotations(nextRot))
    dispatch(setScales(nextScale))
}

export const upsertProductsMeta = (products = []) => {
    const arr = Array.isArray(products) ? products : []
    const meta = {}

    arr.forEach((raw) => {
        const nestedProduct = raw?.product && typeof raw.product === 'object' ? raw.product : null
        const p = nestedProduct ? { ...nestedProduct, ...raw } : raw
        const id = p?._id || p?.id || p?.productId || nestedProduct?._id

        if (!id) return

        meta[String(id)] = {
            _id: String(id),
            id: String(id),
            productId: String(id),
            article: p?.article || p?.sku || '',
            fullName: p?.fullName || p?.name || nestedProduct?.fullName || nestedProduct?.name || '',
            name: p?.name || p?.fullName || nestedProduct?.name || nestedProduct?.fullName || '',
            categorySlug: p?.categorySlug || p?.categoryNameSlug || p?.calculatorData?.categorySlug || '',
            categoryName: p?.categoryName || p?.calculatorData?.categoryName || '',
            reducedImage: p?.reducedImage || p?.img || p?.image || p?.photo || nestedProduct?.reducedImage || '',
            img: p?.img || p?.reducedImage || p?.image || p?.photo || nestedProduct?.img || '',
            image: p?.image || p?.img || p?.reducedImage || nestedProduct?.image || '',
            photo: p?.photo || p?.image || p?.img || p?.reducedImage || nestedProduct?.photo || '',

            price: p?.price ?? p?.bayerPrice ?? p?.productPricing?.basePrice ?? null,
            bayerPrice: p?.bayerPrice ?? null,
            pricePerMeter: p?.pricePerMeter ?? null,
            pricePerPiece: p?.pricePerPiece ?? null,
            productPricing: p?.productPricing || null,
            defaultMaterialKey: p?.defaultMaterialKey || p?.productPricing?.blankMaterialKey || 'gabbro-diabase',
            materialPricingVariants: Array.isArray(p?.materialPricingVariants) ? p.materialPricingVariants : [],

            workPrice: p?.workPrice ?? null,
            workPricePerMeter: p?.workPricePerMeter ?? null,
            workPricePerSquareMeter: p?.workPricePerSquareMeter ?? null,
            workPricePerCubicMeter: p?.workPricePerCubicMeter ?? null,
            workCalcMode: p?.workCalcMode || null,
            millingPricing: p?.millingPricing || null,
            workOptions: Array.isArray(p?.workOptions) ? p.workOptions : [],

            height: p?.height ?? p?.h ?? null,
            width: p?.width ?? p?.w ?? null,
            length: p?.length ?? p?.depth ?? null,
            thickness: p?.thickness ?? p?.depth ?? p?.length ?? null,
            depth: p?.depth ?? p?.length ?? null,
            baseDimensions: (p?.baseDimensions && typeof p.baseDimensions === 'object')
                ? p.baseDimensions
                : (p?.dimensions && typeof p.dimensions === 'object')
                    ? p.dimensions
                    : null,

            fileType: p?.fileType || '',
            modelPath: p?.modelPath || '',
            modelPartsEnabled: p?.modelPartsEnabled === true,
        }
    })

    return {
        type: sceneConstants.UPSERT_PRODUCTS_META,
        payload: meta,
    }
}

export const setMonumentAssemblies = (assemblies = {}) => ({
    type: 'SET_MONUMENT_ASSEMBLIES',
    payload: assemblies,
})

export const setMonumentDecorations = (decorations = {}) => ({
    type: 'SET_MONUMENT_DECORATIONS',
    payload: decorations,
})

export const setMonumentDecorationAssets = (assets = {}) => ({
    type: 'SET_MONUMENT_DECORATION_ASSETS',
    payload: assets,
})

export const setModelPartsMeta = (modelId, patch = {}) => ({
    type: 'SET_MODEL_PARTS_META',
    payload: { modelId, patch },
})

export const setCustomObjects = (customObjects = {}) => ({
    type: sceneConstants.SET_CUSTOM_OBJECTS,
    payload: customObjects,
})

export const createCustomObject = (obj) => ({
    type: sceneConstants.CUSTOM_OBJECT_CREATE,
    payload: obj,
})

export const updateCustomObject = (id, patch = {}) => ({
    type: sceneConstants.CUSTOM_OBJECT_UPDATE,
    payload: { id, patch },
})

export const deleteCustomObject = (id) => ({
    type: sceneConstants.CUSTOM_OBJECT_DELETE,
    payload: id,
})

export const setTransformMode = (mode) => ({
    type: sceneConstants.SET_TRANSFORM_MODE,
    payload: mode,
})

export const setTextureEditMode = (value) => ({
    type: sceneConstants.SET_TEXTURE_EDIT_MODE,
    payload: !!value,
})

export const sceneHistoryCommit = (snapshot) => ({
    type: sceneConstants.SCENE_HISTORY_COMMIT,
    payload: snapshot,
})

export const sceneHistoryUndo = () => ({
    type: sceneConstants.SCENE_HISTORY_UNDO,
})

export const sceneHistoryRedo = () => ({
    type: sceneConstants.SCENE_HISTORY_REDO,
})

export const sceneHistoryJump = (index) => ({
    type: 'SCENE_HISTORY_JUMP',
    payload: index,
})

const ARRAY_PREVIEW_SET_TYPE = 'SET_SCENE_ARRAY_MODIFIER_PREVIEW'
const ARRAY_PREVIEW_CLEAR_TYPE = 'CLEAR_SCENE_ARRAY_MODIFIER_PREVIEW'
const ARRAY_COUNT_MIN = 2
const ARRAY_COUNT_DEFAULT = 5
const ARRAY_COUNT_MAX = 999

const deepCloneSceneValue = (value) => JSON.parse(JSON.stringify(value ?? null))
const arrayGetBaseId = (id) => (typeof id === 'string' && id.includes('__') ? id.split('__')[0] : id)
const uniqSceneList = (arr) => [...new Set((arr || []).filter(Boolean))]

const normalizeSceneArray = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean)
    if (typeof value === 'string' && value) return [value]
    return []
}

const clampArrayCount = (value) => {
    const n = parseInt(value, 10)
    if (!Number.isFinite(n)) return ARRAY_COUNT_DEFAULT
    return Math.min(ARRAY_COUNT_MAX, Math.max(ARRAY_COUNT_MIN, n))
}

const toArrayOffset = (offset = {}) => ({
    x: Number(offset.x) || 0,
    y: Number(offset.y) || 0,
    z: Number(offset.z) || 0,
})

const toRelativeSize = (relativeSize = {}) => ({
    x: Math.max(0.000001, Number(relativeSize.x) || 1),
    y: Math.max(0.000001, Number(relativeSize.y) || 1),
    z: Math.max(0.000001, Number(relativeSize.z) || 1),
})

const toSceneArr3 = (value, fallback = [0, 0, 0]) => {
    if (Array.isArray(value)) return [Number(value[0]) || 0, Number(value[1]) || 0, Number(value[2]) || 0]
    if (value && typeof value === 'object') return [Number(value.x) || 0, Number(value.y) || 0, Number(value.z) || 0]
    return fallback
}

const pickSceneHistorySnapshot = (sceneState) => ({
    selectedModel: sceneState.selectedModel,
    selectedModelPaths: Array.isArray(sceneState.selectedModelPaths) ? sceneState.selectedModelPaths : [],
    selectedModels: sceneState.selectedModels || {},
    instanceIds: sceneState.instanceIds || {},
    copyPaths: sceneState.copyPaths || {},
    modelPaths: Array.isArray(sceneState.modelPaths) ? sceneState.modelPaths : [],
    positions: sceneState.positions || {},
    rotations: sceneState.rotations || {},
    scales: sceneState.scales || {},
    pivotPoints: sceneState.pivotPoints || {},
    pivotPresetById: sceneState.pivotPresetById || {},
    dimensions: sceneState.dimensions || {},
    hiddenModels: sceneState.hiddenModels || {},
    solidObjects: sceneState.solidObjects || {},
    monumentAssemblies: sceneState.monumentAssemblies || {},
    monumentDecorations: sceneState.monumentDecorations || {},
    monumentDecorationAssets: sceneState.monumentDecorationAssets || {},
    modelPartsMeta: sceneState.modelPartsMeta || {},
    customObjects: sceneState.customObjects || {},
    colors: sceneState.colors || {},
    selectionGroups: sceneState.selectionGroups || {},
    activeSelectionGroupId: sceneState.activeSelectionGroupId || null,
    measure: sceneState.measure || {},
    monumentPolished: !!sceneState.monumentPolished,
    modifierPreviews: sceneState.modifierPreviews || { array: null, arraysBySourceId: {}, activeArraySourceId: null },
})


export const applySceneClipboardSnapshot = (snapshot = {}, meta = {}) => (dispatch, getState) => {
    if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
        return { ok: false, error: 'Некорректный snapshot локального буфера' }
    }

    const beforeScene = getState()?.scene || {}

    dispatch(sceneHistoryCommit({
        ...pickSceneHistorySnapshot(beforeScene),
        historyLabel: 'До вставки из буфера',
        historyAt: Date.now(),
        historyKind: 'sceneClipboardBeforePaste',
    }))

    dispatch(hydrateSceneSnapshot(snapshot, {
        source: 'scene-clipboard-paste',
        officeId: meta.officeId || null,
        salesSessionId: meta.sessionId || '',
        sceneContextKey: meta.sceneContextKey || `${meta.officeId || ''}:${meta.sessionId || ''}`,
        preserveHistory: true,
    }))

    const afterScene = getState()?.scene || {}

    dispatch(sceneHistoryCommit({
        ...pickSceneHistorySnapshot(afterScene),
        historyLabel: meta.historyLabel || 'Вставка из буфера',
        historyAt: Date.now(),
        historyKind: 'sceneClipboardPaste',
    }))

    return {
        ok: true,
        selectedModel: afterScene.selectedModel || null,
        selectedModelPaths: Array.isArray(afterScene.selectedModelPaths)
            ? afterScene.selectedModelPaths
            : [],
    }
}

const getFallbackRelativeSize = (scene, targetId) => {
    const baseId = arrayGetBaseId(targetId)
    const dims = scene.dimensions?.[targetId]
        || scene.dimensions?.[baseId]
        || scene.customObjects?.[targetId]?.baseDimensions
        || scene.customObjects?.[baseId]?.baseDimensions
        || { x: 1, y: 1, z: 1 }

    const scale = toSceneArr3(scene.scales?.[targetId] || scene.scales?.[baseId], [1, 1, 1])

    return {
        x: Math.max(0.000001, (Number(dims.x) || 1) * Math.abs(Number(scale[0]) || 1)),
        y: Math.max(0.000001, (Number(dims.y) || 1) * Math.abs(Number(scale[1]) || 1)),
        z: Math.max(0.000001, (Number(dims.z) || 1) * Math.abs(Number(scale[2]) || 1)),
    }
}

const buildArrayDelta = (scene, targetId, options = {}) => {
    const offsetInput = toArrayOffset(options.offset)
    const relative = !!options.relative
    const size = options.relativeSize
        ? toRelativeSize(options.relativeSize)
        : getFallbackRelativeSize(scene, targetId)

    return relative
        ? [offsetInput.x * size.x, offsetInput.y * size.y, offsetInput.z * size.z]
        : [offsetInput.x, offsetInput.y, offsetInput.z]
}

const makeSafeCustomArrayId = (sourceId, index, customObjects) => {
    const safeBase = String(sourceId || 'object')
        .replace(/__/g, '_')
        .replace(/[^a-zA-Z0-9_-]/g, '_')
        .slice(0, 48)

    const stamp = Date.now()
    let candidate = `${safeBase}_array_${stamp}_${index}`
    let guard = 1

    while (customObjects?.[candidate]) {
        candidate = `${safeBase}_array_${stamp}_${index}_${guard}`
        guard += 1
    }

    return candidate
}

export const setSceneArrayModifierPreview = (sourceId, options = {}) => {
    const preview = {
        enabled: !!sourceId,
        sourceId: sourceId || null,
        count: clampArrayCount(options.count),
        relative: options.relative !== false,
        offset: toArrayOffset(options.offset),
        relativeSize: toRelativeSize(options.relativeSize),
    }

    return {
        type: ARRAY_PREVIEW_SET_TYPE,
        payload: preview,
        meta: {
            history: !!options.history,
            historyLabel: options.historyLabel || 'Массив: предпросмотр',
            historyKind: options.historyKind || 'arrayPreviewSet',
            historyPayload: {
                sourceId: sourceId || null,
                preview,
            },
            historyAt: options.historyAt || Date.now(),
        },
    }
}

export const clearSceneArrayModifierPreview = (sourceId = null, options = {}) => ({
    type: ARRAY_PREVIEW_CLEAR_TYPE,
    payload: { sourceId: sourceId || null },
    meta: {
        history: !!options.history,
        historyLabel: options.historyLabel || 'Массив: отмена',
        historyKind: options.historyKind || 'arrayPreviewClear',
        historyPayload: {
            sourceId: sourceId || null,
        },
        historyAt: options.historyAt || Date.now(),
    },
})

const removeSceneArrayPreview = (modifierPreviews = {}, sourceId = null) => {
    const sourceMap = modifierPreviews?.arraysBySourceId && typeof modifierPreviews.arraysBySourceId === 'object'
        ? modifierPreviews.arraysBySourceId
        : {}
    const arraysBySourceId = { ...sourceMap }
    const removeId = sourceId || modifierPreviews?.activeArraySourceId || modifierPreviews?.array?.sourceId || null

    if (removeId) delete arraysBySourceId[removeId]

    const ids = Object.keys(arraysBySourceId)
    const previousActive = modifierPreviews?.activeArraySourceId || null
    const activeArraySourceId = previousActive && previousActive !== removeId && arraysBySourceId[previousActive]
        ? previousActive
        : (ids[0] || null)

    return {
        array: activeArraySourceId ? arraysBySourceId[activeArraySourceId] : null,
        arraysBySourceId,
        activeArraySourceId,
    }
}

const mergeSceneMap = (source = {}, patch = {}) => (
    Object.keys(patch).length ? { ...(source || {}), ...patch } : (source || {})
)

export const toggleSceneObjectsSolid = (ids = []) => (dispatch, getState) => {
    const list = [...new Set((Array.isArray(ids) ? ids : [ids]).filter(Boolean))]

    if (!list.length) {
        return { ok: false, error: 'Выберите объект, который нужно сделать твёрдым' }
    }

    const scene = getState()?.scene || {}
    const current = scene.solidObjects || {}
    const nextSolid = !list.every((id) => current[id] === true)

    dispatch(setSceneObjectsSolid(list, nextSolid))

    const finalScene = getState()?.scene || {}
    dispatch(sceneHistoryCommit({
        ...pickSceneHistorySnapshot(finalScene),
        historyLabel: nextSolid ? 'Твёрдые объекты' : 'Проходимые объекты',
        historyAt: Date.now(),
    }))

    return { ok: true, solid: nextSolid, ids: list }
}

export const applySceneArrayModifier = (targetId, options = {}) => (dispatch, getState) => {
    const scene = getState()?.scene || {}
    const count = clampArrayCount(options.count)
    const copyCount = Math.max(0, count - 1)
    const skipHistory = options.skipHistory === true
    let createdIds = []

    if (!targetId || copyCount <= 0) {
        return { ok: false, error: 'Не удалось определить объект для копирования', createdIds: [] }
    }

    const delta = buildArrayDelta(scene, targetId, options)
    const sourcePosition = toSceneArr3(scene.positions?.[targetId], [0, 0, 0])
    const sourceRotation = toSceneArr3(scene.rotations?.[targetId], [0, 0, 0])
    const sourceScale = toSceneArr3(scene.scales?.[targetId], [1, 1, 1])
    const sourcePivot = toSceneArr3(scene.pivotPoints?.[targetId], [0, 0, 0])
    const sourceDimensions = scene.dimensions?.[targetId]
        || scene.dimensions?.[arrayGetBaseId(targetId)]
        || null
    const nextPositions = {}
    const nextRotations = {}
    const nextScales = {}
    const nextPivotPoints = {}
    const nextDimensions = {}
    const nextModifierPreviews = removeSceneArrayPreview(scene.modifierPreviews || {}, targetId)
    const nextSolidObjects = { ...(scene.solidObjects || {}) }
    const nextMonumentAssemblies = { ...(scene.monumentAssemblies || {}) }
    const nextMonumentDecorations = { ...(scene.monumentDecorations || {}) }
    const sourceIsSolid = nextSolidObjects[targetId] === true
    const isCustomTarget = !!scene.customObjects?.[targetId]

    if (isCustomTarget) {
        const nextCustomObjects = { ...(scene.customObjects || {}) }
        const newIds = []

        for (let index = 1; index <= copyCount; index += 1) {
            const newId = makeSafeCustomArrayId(targetId, index, nextCustomObjects)
            const sourceObject = deepCloneSceneValue(scene.customObjects[targetId]) || {}

            nextCustomObjects[newId] = {
                ...sourceObject,
                id: newId,
                name: sourceObject.name ? `${sourceObject.name} ${index + 1}` : `Копия ${index + 1}`,
            }

            newIds.push(newId)
            nextPositions[newId] = [
                sourcePosition[0] + delta[0] * index,
                sourcePosition[1] + delta[1] * index,
                sourcePosition[2] + delta[2] * index,
            ]
            nextRotations[newId] = [...sourceRotation]
            nextScales[newId] = [...sourceScale]
            nextPivotPoints[newId] = [...sourcePivot]

            if (sourceDimensions) nextDimensions[newId] = deepCloneSceneValue(sourceDimensions)
            if (sourceIsSolid) nextSolidObjects[newId] = true
        }

        createdIds = [...newIds]

        dispatch(replaceSceneInstanceState({
            customObjects: nextCustomObjects,
            positions: mergeSceneMap(scene.positions, nextPositions),
            rotations: mergeSceneMap(scene.rotations, nextRotations),
            scales: mergeSceneMap(scene.scales, nextScales),
            pivotPoints: mergeSceneMap(scene.pivotPoints, nextPivotPoints),
            dimensions: mergeSceneMap(scene.dimensions, nextDimensions),
            solidObjects: nextSolidObjects,
            modifierPreviews: nextModifierPreviews,
            selectedModel: newIds[newIds.length - 1] || targetId,
            selectedModelPaths: newIds.length ? [...newIds] : [targetId],
        }))
    } else {
        const records = buildSceneInstanceRecords(
            scene.selectedModels || {},
            scene.instanceIds || {},
            scene.copyPaths || {}
        )
        const detail = records.find((record) => record.path === targetId)

        if (!detail?.categoryKey) {
            return { ok: false, error: 'Для выбранного объекта нельзя создать копию', createdIds: [] }
        }

        const categoryKey = detail.categoryKey
        const baseId = detail.productId
        const nextSelectedModels = { ...(scene.selectedModels || {}) }
        const currentCategoryValue = nextSelectedModels[categoryKey]
        const categoryArray = normalizeSceneArray(currentCategoryValue)

        nextSelectedModels[categoryKey] = [
            ...categoryArray,
            ...new Array(copyCount).fill(baseId),
        ]

        const nextInstanceIds = appendSceneInstanceIds(
            scene.selectedModels || {},
            scene.instanceIds || {},
            categoryKey,
            baseId,
            copyCount,
            createSceneInstanceSeed()
        )
        const currentCopyPaths = ensureSceneCopyPaths(
            scene.selectedModels || {},
            scene.instanceIds || {},
            scene.copyPaths || {}
        )
        const usedSuffixes = new Set(
            records
                .filter((record) => record.productId === baseId)
                .map((record) => record.copyPath)
        )
        const newSuffixes = allocateSceneCopySuffixes(
            usedSuffixes,
            detail.copyPath || '',
            copyCount
        )
        const nextCategoryInstanceIds = Array.isArray(nextInstanceIds?.[categoryKey])
            ? nextInstanceIds[categoryKey]
            : []
        const newInstanceIds = nextCategoryInstanceIds.slice(
            categoryArray.length,
            categoryArray.length + copyCount
        )
        const nextCopyPaths = { ...currentCopyPaths }
        const newPaths = []

        newInstanceIds.forEach((instanceId, index) => {
            const suffix = newSuffixes[index]
            if (!instanceId || suffix === undefined) return

            nextCopyPaths[instanceId] = suffix
            newPaths.push(getSceneInstancePath(baseId, suffix))
        })

        if (newPaths.length !== copyCount) {
            return {
                ok: false,
                error: 'Не удалось выделить стабильные ID для новых копий',
                createdIds: [],
            }
        }

        createdIds = [...newPaths]

        newPaths.forEach((path, index) => {
            const step = index + 1
            nextPositions[path] = [
                sourcePosition[0] + delta[0] * step,
                sourcePosition[1] + delta[1] * step,
                sourcePosition[2] + delta[2] * step,
            ]
            nextRotations[path] = [...sourceRotation]
            nextScales[path] = [...sourceScale]
            nextPivotPoints[path] = [...sourcePivot]

            if (sourceDimensions) nextDimensions[path] = deepCloneSceneValue(sourceDimensions)
            if (sourceIsSolid) nextSolidObjects[path] = true

            if (scene.monumentAssemblies?.[targetId]) {
                nextMonumentAssemblies[path] = remapSceneObjectReferences(
                    deepCloneSceneValue(scene.monumentAssemblies[targetId]),
                    { [targetId]: path }
                )
            }

            if (scene.monumentDecorations?.[targetId]) {
                nextMonumentDecorations[path] = remapSceneObjectReferences(
                    deepCloneSceneValue(scene.monumentDecorations[targetId]),
                    { [targetId]: path }
                )
            }
        })

        const nextColors = { ...(scene.colors || {}) }
        const categoryColors = Array.isArray(scene.colors?.[categoryKey])
            ? scene.colors[categoryKey]
            : null

        if (categoryColors) {
            const sourceColor = categoryColors[detail.index] !== undefined
                ? categoryColors[detail.index]
                : (categoryColors[0] !== undefined ? categoryColors[0] : { 0: 'original' })

            nextColors[categoryKey] = [
                ...categoryColors,
                ...newPaths.map(() => deepCloneSceneValue(sourceColor)),
            ]
        }

        const instanceColor = scene.colors?.instanceByPath?.[targetId]

        if (instanceColor !== undefined) {
            const instanceByPath = { ...(scene.colors?.instanceByPath || {}) }

            newPaths.forEach((path) => {
                instanceByPath[path] = deepCloneSceneValue(instanceColor)
            })

            nextColors.instanceByPath = instanceByPath
        }

        dispatch(replaceSceneInstanceState({
            selectedModels: nextSelectedModels,
            instanceIds: nextInstanceIds,
            copyPaths: nextCopyPaths,
            positions: mergeSceneMap(scene.positions, nextPositions),
            rotations: mergeSceneMap(scene.rotations, nextRotations),
            scales: mergeSceneMap(scene.scales, nextScales),
            pivotPoints: mergeSceneMap(scene.pivotPoints, nextPivotPoints),
            dimensions: mergeSceneMap(scene.dimensions, nextDimensions),
            solidObjects: nextSolidObjects,
            monumentAssemblies: nextMonumentAssemblies,
            monumentDecorations: nextMonumentDecorations,
            colors: nextColors,
            modifierPreviews: nextModifierPreviews,
            selectedModel: newPaths[newPaths.length - 1] || targetId,
            selectedModelPaths: [...newPaths],
        }))

        if (typeof console !== 'undefined') {
            console.log('[SCENE-IDENTITY][copy]', {
                sourcePath: targetId,
                sourceInstanceId: detail.instanceId,
                sourceCopyPath: detail.copyPath,
                createdPaths: newPaths,
                createdInstanceIds: newInstanceIds,
            })
        }
    }

    if (!skipHistory) {
        const finalScene = getState()?.scene || {}

        dispatch(sceneHistoryCommit({
            ...pickSceneHistorySnapshot(finalScene),
            historyLabel: 'Массив',
            historyAt: Date.now(),
        }))
    }

    return { ok: true, createdIds }
}

export const applySceneArrayModifierPreview = (sourceId = null) => (dispatch, getState) => {
    const modifierPreviews = getState()?.scene?.modifierPreviews || {}
    const arraysBySourceId = modifierPreviews.arraysBySourceId || {}
    const activeSourceId = sourceId || modifierPreviews.activeArraySourceId || modifierPreviews.array?.sourceId || null
    const preview = activeSourceId
        ? (arraysBySourceId[activeSourceId] || (modifierPreviews.array?.sourceId === activeSourceId ? modifierPreviews.array : null))
        : (modifierPreviews.array || null)

    if (!preview?.enabled || !preview?.sourceId) return

    dispatch(applySceneArrayModifier(preview.sourceId, preview))
}

const REPLACE_SCENE_INSTANCE_STATE_TYPE = 'REPLACE_SCENE_INSTANCE_STATE'

export const replaceSceneInstanceState = (patch = {}) => ({
    type: REPLACE_SCENE_INSTANCE_STATE_TYPE,
    payload: patch,
    meta: {
        sceneInstanceSeed: createSceneInstanceSeed(),
    },
})

// selectedModel + selectedModelPaths are one logical selection snapshot.
// Update them with one Redux action so UI panels and the R3F scene can never
// observe different halves of the same click.
export const setSceneSelection = (activeId = null, ids = null) => {
    const requestedIds = Array.isArray(ids)
        ? ids
        : (activeId ? [activeId] : [])
    const selectedModelPaths = uniqSceneList(requestedIds)
    let selectedModel = activeId || null

    if (selectedModel && !selectedModelPaths.includes(selectedModel)) {
        selectedModelPaths.push(selectedModel)
    }

    if (!selectedModel && selectedModelPaths.length) {
        selectedModel = selectedModelPaths[selectedModelPaths.length - 1] || null
    }

    return replaceSceneInstanceState({
        selectedModel,
        selectedModelPaths,
    })
}

export const patchSceneTransforms = (patch = {}) => ({
    type: 'PATCH_SCENE_TRANSFORMS',
    payload: patch,
})

export const removeSceneTargets = (targets = [], options = {}) => (dispatch, getState) => {
    const scene = getState()?.scene || {}
    const list = uniqSceneList(Array.isArray(targets) ? targets : [targets])

    if (!list.length) {
        return { ok: false, error: 'Выберите объект для удаления', removedIds: [] }
    }

    const records = buildSceneInstanceRecords(
        scene.selectedModels || {},
        scene.instanceIds || {},
        scene.copyPaths || {}
    )
    const recordsByPath = new Map(records.map((record) => [record.path, record]))
    const customIds = list.filter((id) => !!scene.customObjects?.[id])
    const standardRecords = list
        .map((id) => recordsByPath.get(id))
        .filter(Boolean)

    if (!customIds.length && !standardRecords.length) {
        return {
            ok: false,
            error: 'Выбранные объекты нельзя удалить этим способом',
            removedIds: [],
        }
    }

    const removePathSet = new Set(standardRecords.map((record) => record.path))
    const removeInstanceIdSet = new Set(
        standardRecords.map((record) => record.instanceId).filter(Boolean)
    )

    const nextCustomObjects = { ...(scene.customObjects || {}) }
    customIds.forEach((id) => delete nextCustomObjects[id])

    const nextSelectedModels = { ...(scene.selectedModels || {}) }
    const removeIndexesByCategory = {}

    standardRecords.forEach((record) => {
        if (!record.categoryKey) return
        if (!removeIndexesByCategory[record.categoryKey]) {
            removeIndexesByCategory[record.categoryKey] = new Set()
        }
        removeIndexesByCategory[record.categoryKey].add(record.index)
    })

    Object.entries(removeIndexesByCategory).forEach(([categoryKey, indexes]) => {
        const previousValue = nextSelectedModels[categoryKey]
        const previousArray = normalizeSceneArray(previousValue)
        const nextArray = previousArray.filter((_value, index) => !indexes.has(index))

        nextSelectedModels[categoryKey] = Array.isArray(previousValue)
            ? nextArray
            : (nextArray[0] || null)
    })

    const nextInstanceIds = removeSceneInstanceIds(
        scene.selectedModels || {},
        scene.instanceIds || {},
        removeIndexesByCategory
    )
    const nextCopyPaths = {
        ...ensureSceneCopyPaths(
            scene.selectedModels || {},
            scene.instanceIds || {},
            scene.copyPaths || {}
        ),
    }

    removeInstanceIdSet.forEach((instanceId) => {
        delete nextCopyPaths[instanceId]
    })

    const removeAndClean = (source = {}) => {
        const next = { ...(source || {}) }

        removePathSet.forEach((path) => delete next[path])
        customIds.forEach((id) => delete next[id])

        return next
    }

    const nextColors = {
        ...(scene.colors || {}),
        instanceByPath: removeAndClean(scene.colors?.instanceByPath || {}),
        // Product-level colors remain keyed by product/base id.
        byModelPath: { ...(scene.colors?.byModelPath || {}) },
    }

    Object.entries(removeIndexesByCategory).forEach(([categoryKey, indexes]) => {
        if (!Array.isArray(scene.colors?.[categoryKey])) return

        nextColors[categoryKey] = scene.colors[categoryKey]
            .filter((_value, index) => !indexes.has(index))
    })

    let nextModifierPreviews = scene.modifierPreviews || {}
    const removedSourceIds = [...removePathSet, ...customIds]

    if (removedSourceIds.length) {
        const arraysBySourceId = {
            ...(nextModifierPreviews.arraysBySourceId || {}),
        }

        removedSourceIds.forEach((id) => delete arraysBySourceId[id])

        const activeIds = Object.keys(arraysBySourceId)
        const activeArraySourceId = arraysBySourceId[nextModifierPreviews.activeArraySourceId]
            ? nextModifierPreviews.activeArraySourceId
            : (activeIds[0] || null)

        nextModifierPreviews = {
            array: activeArraySourceId ? arraysBySourceId[activeArraySourceId] : null,
            arraysBySourceId,
            activeArraySourceId,
        }
    }

    const removedSet = new Set(removedSourceIds)
    const nextSelectedPaths = uniqSceneList(
        (scene.selectedModelPaths || []).filter((path) => !removedSet.has(path))
    )
    const previousSelectedModel = scene.selectedModel || null
    const survivingSelectedModel = previousSelectedModel && !removedSet.has(previousSelectedModel)
        ? previousSelectedModel
        : null
    const nextSelectedModel = options.keepSelection === true
        ? (survivingSelectedModel || nextSelectedPaths[nextSelectedPaths.length - 1] || null)
        : null
    const finalSelectedPaths = nextSelectedModel ? nextSelectedPaths : []

    if (typeof console !== 'undefined') {
        console.log('[SCENE-IDENTITY][delete]', {
            requested: list,
            standard: standardRecords.map((record) => ({
                path: record.path,
                instanceId: record.instanceId,
                copyPath: record.copyPath,
                categoryKey: record.categoryKey,
                index: record.index,
            })),
            beforePaths: records.map((record) => record.path),
            survivingInstanceIds: nextInstanceIds,
            survivingCopyPaths: nextCopyPaths,
        })
    }

    dispatch(replaceSceneInstanceState({
        selectedModels: nextSelectedModels,
        instanceIds: nextInstanceIds,
        copyPaths: nextCopyPaths,
        customObjects: nextCustomObjects,
        positions: removeAndClean(scene.positions || {}),
        rotations: removeAndClean(scene.rotations || {}),
        scales: removeAndClean(scene.scales || {}),
        pivotPoints: removeAndClean(scene.pivotPoints || {}),
        pivotPresetById: removeAndClean(scene.pivotPresetById || {}),
        dimensions: removeAndClean(scene.dimensions || {}),
        hiddenModels: removeAndClean(scene.hiddenModels || {}),
        solidObjects: removeAndClean(scene.solidObjects || {}),
        monumentAssemblies: removeAndClean(scene.monumentAssemblies || {}),
        monumentDecorations: removeAndClean(scene.monumentDecorations || {}),
        modelPartsMeta: removeAndClean(scene.modelPartsMeta || {}),
        colors: nextColors,
        modifierPreviews: nextModifierPreviews,
        selectedModel: nextSelectedModel,
        selectedModelPaths: finalSelectedPaths,
    }))

    if (options.skipHistory !== true) {
        const finalScene = getState()?.scene || {}

        dispatch(sceneHistoryCommit({
            ...pickSceneHistorySnapshot(finalScene),
            historyLabel: list.length > 1 ? 'Удаление объектов' : 'Удаление объекта',
            historyAt: Date.now(),
        }))
    }

    return {
        ok: true,
        removedIds: [...removePathSet, ...customIds],
    }
}

export const mirrorSceneTargets = (targets = [], axis = 'x', options = {}) => (dispatch, getState) => {
    const normalizedAxis = ['x', 'y', 'z'].includes(String(axis || '').toLowerCase())
        ? String(axis).toLowerCase()
        : 'x'
    const index = normalizedAxis === 'y' ? 1 : normalizedAxis === 'z' ? 2 : 0
    const scene = getState()?.scene || {}
    const list = uniqSceneList(Array.isArray(targets) ? targets : [targets])

    if (!list.length) {
        return { ok: false, error: 'Выберите объект для зеркального отражения' }
    }

    const nextScales = {}

    list.forEach((path) => {
        const current = toSceneArr3(scene.scales?.[path], [1, 1, 1])
        const next = [...current]
        const value = Number(next[index])
        next[index] = (Number.isFinite(value) && value !== 0 ? value : 1) * -1
        nextScales[path] = next
    })

    dispatch(setScales(nextScales))

    if (options.skipHistory !== true) {
        const finalScene = getState()?.scene || {}
        dispatch(sceneHistoryCommit({
            ...pickSceneHistorySnapshot(finalScene),
            historyLabel: `Зеркало ${normalizedAxis.toUpperCase()}`,
            historyAt: Date.now(),
        }))
    }

    return { ok: true, targets: list }
}

export const createSceneMirrorCopies = (targets = [], axis = 'x', options = {}) => (dispatch, getState) => {
    const normalizedAxis = ['x', 'y', 'z'].includes(String(axis || '').toLowerCase())
        ? String(axis).toLowerCase()
        : 'x'
    const list = uniqSceneList(Array.isArray(targets) ? targets : [targets])
    const direction = Number(options.direction) < 0 ? -1 : 1
    const gapMM = Math.max(0, Number(options.gapMM) || 0)
    const maxSceneObjects = Math.max(1, Number(options.maxSceneObjects || 5000))
    const initialScene = getState()?.scene || {}
    const currentObjectCount = Array.isArray(initialScene.modelPaths)
        ? initialScene.modelPaths.length
        : 0

    if (!list.length) {
        return { ok: false, error: 'Выберите объект для зеркальной копии', copyIds: [] }
    }

    if (currentObjectCount + list.length > maxSceneObjects) {
        return {
            ok: false,
            error: `Лимит сцены — ${maxSceneObjects} объектов`,
            copyIds: [],
        }
    }

    const copyIds = []

    list.forEach((targetId) => {
        const scene = getState()?.scene || {}
        const actualSize = getFallbackRelativeSize(scene, targetId)
        const axisSize = Math.max(0.000001, Number(actualSize?.[normalizedAxis]) || 1)
        const centerOffset = direction * (axisSize + gapMM / 1000)
        const offset = { x: 0, y: 0, z: 0 }
        offset[normalizedAxis] = centerOffset

        const duplicateResult = dispatch(applySceneArrayModifier(targetId, {
            count: 2,
            relative: false,
            offset,
            skipHistory: true,
        }))
        const created = Array.isArray(duplicateResult?.createdIds)
            ? duplicateResult.createdIds.filter(Boolean)
            : []

        if (!created.length) return

        dispatch(mirrorSceneTargets(created, normalizedAxis, { skipHistory: true }))
        copyIds.push(...created)
    })

    if (!copyIds.length) {
        return { ok: false, error: 'Не удалось создать зеркальную копию', copyIds: [] }
    }

    dispatch(setSceneSelection(copyIds[copyIds.length - 1], copyIds))

    const finalScene = getState()?.scene || {}
    dispatch(sceneHistoryCommit({
        ...pickSceneHistorySnapshot(finalScene),
        historyLabel: `Зеркальная копия ${normalizedAxis.toUpperCase()}`,
        historyAt: Date.now(),
    }))

    return { ok: true, copyIds }
}

export const setSceneObjectQuantity = (targetId, requestedQuantity, options = {}) => (dispatch, getState) => {
    let scene = getState()?.scene || {}
    const quantity = Math.max(1, Math.min(
        Math.max(1, Number(options.maxQuantity || options.maxSceneObjects || 999)),
        Math.round(Number(requestedQuantity) || 1)
    ))

    if (!targetId) {
        return { ok: false, error: 'Выберите объект, количество которого нужно изменить' }
    }

    if (scene.customObjects?.[targetId]) {
        return { ok: false, error: 'Количество пользовательских объектов меняется через массив' }
    }

    const records = buildSceneInstanceRecords(
        scene.selectedModels || {},
        scene.instanceIds || {},
        scene.copyPaths || {}
    )
    const targetDetail = records.find((record) => record.path === targetId)

    if (!targetDetail?.categoryKey || !targetDetail?.productId) {
        return { ok: false, error: 'Для выбранного объекта количество изменить нельзя' }
    }

    const matchingRecords = records
        .filter((record) => (
            record.categoryKey === targetDetail.categoryKey &&
            record.productId === targetDetail.productId
        ))
        .sort((a, b) => a.index - b.index)
    const currentQuantity = matchingRecords.length

    if (!currentQuantity || quantity === currentQuantity) {
        return { ok: true, quantity: currentQuantity }
    }

    const maxSceneObjects = Math.max(1, Number(options.maxSceneObjects || 5000))

    if (quantity > currentQuantity) {
        const addCount = quantity - currentQuantity
        const currentPathCount = Array.isArray(scene.modelPaths) ? scene.modelPaths.length : 0

        if (currentPathCount + addCount > maxSceneObjects) {
            return {
                ok: false,
                error: `Лимит сцены — ${maxSceneObjects} объектов`,
            }
        }

        dispatch(applySceneArrayModifier(targetId, {
            count: addCount + 1,
            relative: true,
            offset: options.offset || { x: 1.1, y: 0, z: 0 },
            relativeSize: options.relativeSize,
        }))

        return { ok: true, quantity }
    }

    const keepRecords = []
    const removeRecords = []
    const preferredTarget = matchingRecords.find((item) => item.path === targetId)

    if (preferredTarget) keepRecords.push(preferredTarget)

    matchingRecords.forEach((record) => {
        if (record.path === preferredTarget?.path) return
        if (keepRecords.length < quantity) keepRecords.push(record)
        else removeRecords.push(record)
    })

    while (keepRecords.length > quantity) {
        removeRecords.push(keepRecords.pop())
    }

    const removal = dispatch(removeSceneTargets(
        removeRecords.map((record) => record.path),
        { skipHistory: true }
    ))

    if (!removal?.ok) {
        return removal || { ok: false, error: 'Не удалось изменить количество объектов' }
    }

    scene = getState()?.scene || {}
    dispatch(sceneHistoryCommit({
        ...pickSceneHistorySnapshot(scene),
        historyLabel: 'Количество объектов',
        historyAt: Date.now(),
    }))

    return { ok: true, quantity }
}

export const setSunAngle = (angle) => ({
    type: sceneConstants.SET_SUN_ANGLE,
    payload: angle,
})

export const setSunVertical = (angle) => ({
    type: sceneConstants.SET_SUN_VERTICAL,
    payload: angle,
})

export const setSunIntensity = (intensity) => ({
    type: sceneConstants.SET_SUN_INTENSITY,
    payload: intensity,
})

export const setShadowsEnabled = (enabled) => ({
    type: sceneConstants.SET_SHADOWS_ENABLED,
    payload: !!enabled,
})

export const setGraphicsQuality = (quality) => ({
    type: sceneConstants.SET_GRAPHICS_QUALITY,
    payload: normalizeGraphicsQuality(quality),
})
